import React from "react";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { closeModal } from "../../redux/slices/uiSlice";
import ModalWrapper from "./ModalWrapper";
import { REGEX } from "../../utils/constant";
import { createInquiryAPI } from "../../api/user";
import useToast from "../../hooks/useToast";
import { useTranslation } from "react-i18next";

const ContactUsModal: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const contactUsValidationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required(t("validation.nameRequired"))
      .min(2, t("validation.nameMin"))
      .max(50, t("validation.nameMax")),
    email: Yup.string().matches(REGEX.EMAIL, t("validation.emailValid")).required(t("validation.emailRequired")),
    message: Yup.string()
      .trim()
      .required(t("validation.messageRequired"))
      .min(10, t("validation.messageMin"))
      .max(500, t("validation.messageMax")),
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createInquiryAPI,
    onSuccess: () => {
      useToast(t("messages.messageSent"), "success");
      dispatch(closeModal());
    },
    onError: (error: Error) => {
      console.error("Contact Us Error:", error.message);
      useToast(error.message || t("messages.failedToSend"), "error");
    },
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      message: "",
    },
    validationSchema: contactUsValidationSchema,
    onSubmit: (values) => {
      mutate(values);
    },
  });

  const handleClose = () => {
    dispatch(closeModal());
  };

  return (
    <ModalWrapper onClose={handleClose}>
      <div className="flex justify-between -mt-4">
        <h4 className="2xl:text-[1.563vw]/[1.7vw] xl:text-3xl/6 text-2xl/5 font-semibold text-primary-orange mb-0">
          {t("contactUs.contactUs")}
        </h4>
      </div>
      <hr className="border-0 2xl:h-[0.053vw] h-[1px] w-full 2xl:mb-[1vw] md:mb-4 mb-3 2xl:mt-[0.98vw] mt-4 [background:linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
      <p className="2xl:text-[0.93vw]/[1vw] md:text-lg/tight text-base/tight text-black-800 font-bold text-center 2xl:mb-[1vw] md:mb-4 mb-3">
        {t("contactUs.weAreHere")}
      </p>

      {/* Form */}
      <form onSubmit={formik.handleSubmit}>
        {/* Full Name Field */}
        <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
          <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
            2xl:rounded-[0.26vw] rounded-[5px]
            skew-x-[-10deg]
            2xl:border-[0.104vw] border-[1.5px]
            transition-all
            ${formik.touched.name && formik.errors.name
              ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
              : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            }
          `}>
            <input
              type="text"
              name="name"
              placeholder={t("contactUs.enterFullName")}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isPending}
              className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent ${formik.touched.name && formik.errors.name ? "!border-red-500" : ""
                }`}
            />
          </div>
          {formik.touched.name && formik.errors.name && (
            <p className="text-red-500 lg:text-[0.729vw] text-md lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
              {formik.errors.name}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
          <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
            2xl:rounded-[0.26vw] rounded-[5px]
            skew-x-[-10deg]
            2xl:border-[0.104vw] border-[1.5px]
            transition-all
            ${formik.touched.email && formik.errors.email
              ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
              : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            }
          `}>
            <input
              type="text"
              name="email"
              placeholder={t("contactUs.enterEmail")}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isPending}
              className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent ${formik.touched.email && formik.errors.email ? "!border-red-500" : ""
                }`}
            />
          </div>
          {formik.touched.email && formik.errors.email && (
            <p className="text-red-500 lg:text-[0.729vw] text-md lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
              {formik.errors.email}
            </p>
          )}
        </div>

        {/* Description/Message Field */}
        <div className="lg:mb-[1.1vw] md:mb-5 mb-4">
          <div className={`relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex
            2xl:rounded-[0.26vw] rounded-[5px]
            skew-x-[-10deg]
            2xl:border-[0.104vw] border-[1.5px]
            transition-all
            ${formik.touched.message && formik.errors.message
              ? "border-red-500 2xl:shadow-[0px_0.156vw_0px_#fb2c36] shadow-[0px_2px_0px_#fb2c36]"
              : "border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
            }
          `}>
            <textarea
              name="message"
              placeholder={t("contactUs.writeDescription")}
              rows={4}
              value={formik.values.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isPending}
              className={`2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full  lg:text-[1.1vw]/none md:text-base/none text-sm/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent resize-none hide-scroll`}
            />
          </div>
          {formik.touched.message && formik.errors.message && (
            <p className="text-red-500 lg:text-[0.729vw] text-md lg:mt-[0.208vw] mt-1 lg:px-[0.833vw] px-4 font-bold">
              {formik.errors.message}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isPending}
          className="w-full 2xl:rounded-[0.26vw] rounded-[5px] h-full flex
    skew-x-[-10deg]
    2xl:border-[0.104vw] border-[1.5px] lg:text-[1.1vw]/[1.4vw] md:text-lg/tight text-base/tight font-bold text-primary-yellow bg-primary-yellow border-black-300 transition-all duration-200 cursor-pointer text-center disabled:opacity-50 disabled:cursor-not-allowed 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]"
        >
          <span className="2xl:pt-[0.7vw] pt-2.5 2xl:m-[0.208vw] m-1 !mt-0 2xl:py-[0.5vw] py-2 lg:px-[5%] px-4 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[0deg] !border-t-0 2xl:border-[0.104vw] border-[1.5px] border-black-300 bg-[linear-gradient(180deg,#D76A4D_0%,#B35840_101.89%)] w-full h-full"><span className="skew-x-[10deg] block">
            {isPending ? (
              <span className="flex items-center justify-center lg:gap-[0.417vw] gap-2">
                <svg
                  className="animate-spin lg:w-[1.042vw] lg:h-[1.042vw] h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {t("contactUs.sending")}
              </span>
            ) : (
              t("contactUs.submit")
            )}
          </span></span>
        </button>
      </form>
    </ModalWrapper>
  );
};

export default ContactUsModal;
